/* =========================================================================
   Company-benefit labels, tiered by where they actually apply.
   Finland tier sources: official Vero decisions, Edenred/ePassi product pages,
   Kela, PAM and STM.  Updated: May 2025.
   US/Canada/EU-general tiers added 2026-09-03 as part of internationalizing the
   benefits taxonomy: see BENEFIT_REGION_TIERS below for which tier each label
   belongs to, and utils/data/processor-output-fields.ts's filterBenefitsToJobCountry
   for how that tier is used to keep only benefits that make sense for a given job's
   resolved country (e.g. never show "401(k) matching" on a Finland job).
   ====================================================================== */

/* ---------------- ENGLISH ------------------------------------------------ */
export const BENEFITS_EN: string[] = [
  /* https://www.smartum.fi/en/latest/tyosuhde-etujen-verorajat-vuonna-2025 */
  'Lunch benefit',

  /* https://benefits.workday.com/fi/sports-and-culture-vouchers. Also genuinely applies to
     Sweden: Skatteverket's tax-free "friskvårdsbidrag" (personnel wellness benefit, up to
     5,000 SEK/year) covers the same real-world activities (gym, sports, massage, destressing).
     See BENEFIT_REGION_TIERS below for why this label carries both a 'finland' and 'sweden' tag
     instead of being duplicated into a separate Swedish-only label.
     https://www.skatteverket.se/foretag/arbetsgivare/lonochersattning/formaner/personalvardsformanmotionochfriskvard.4.3016b5d91791bf546791431.html */
  'Sports & culture benefit',

  /* https://edenred.fi/en/employer/employee-benefit-taxation. Also covered under Sweden's
     friskvårdsbidrag (same Skatteverket source as Sports & culture benefit above), which
     explicitly includes massage as an eligible wellness activity. */
  'Massage benefit',

  /* https://www.epassi.fi/en/home */
  'Well-being / healthcare wallet',

  /* https://www.vero.fi/en/incomes-register/companies-and-organisations/employers/earnings-payment-data/fringe-benefits/bicycle-benefit-and-employer-subsidised-commuter-ticket/ */
  'Commuter pass',

  /* https://www.vero.fi/en/detailed-guidance/decisions/47380/in-kind-benefits-fringe-benefits-2025/ */
  'Company bike',

  /* Every Finnish employer must provide basic (preventive-only) occupational healthcare by law
     (Työterveyshuoltolaki), so listing that statutory floor as a "benefit" isn't a real
     differentiator. This label means going beyond it: Kela's employer reimbursement categories
     split statutory preventive care (class I) from voluntary general medical care (class II,
     sairaanhoito) employers can optionally add, same "above the baseline" framing as Extra
     pension / Extra health insurance elsewhere in this file.
     https://stm.fi/en/occupational-health-care ; https://www.kela.fi/occupational-health-care */
  'Extended occupational healthcare',

  /* https://www.vero.fi/en/individuals/tax-cards-and-tax-returns/income/earned-income/fringe-benefits-from-employment/ */
  'Extra health insurance',

  /* https://edenred.fi/en/employer/employee-benefit-taxation */
  'Work phone',

  /* https://edenred.fi/en/employer/employee-benefit-taxation */
  'Home-internet benefit',

  /* https://www.vero.fi/en/individuals/deductions/remote-working-and-deductions/ */
  'Remote-work allowance',

  /* https://stm.fi/en/working-hours-act */
  'Flexible working hours',

  /* https://edenred.fi/en/employer/employee-benefit-taxation */
  'Training budget',

  /* https://edenred.fi/en/employer/employee-benefit-taxation */
  'Cash bonus',

  /* https://edenred.fi/en/employer/employee-benefit-taxation */
  'Stock options / RSU',

  /* https://edenred.fi/en/employer/employee-benefit-taxation */
  'Extra pension',

  /* https://edenred.fi/en/employer/employee-benefit-taxation */
  'Accident / life insurance',

  /* https://www.pam.fi/en/working-life/guide-to-working-life/employment-relationships/wages-and-holiday-bonus/ */
  'Holiday bonus',

  /* https://www.kela.fi/private-day-care-allowance */
  'Child-care voucher',

  /* https://www.vero.fi/en/detailed-guidance/decisions/47380/in-kind-benefits-fringe-benefits-2025/ */
  'Company car',

  /* US: IRS-governed employer-sponsored retirement plan with employer matching contributions */
  '401(k) matching',

  /* US: IRS-governed tax-advantaged medical savings account, typically paired with a high-deductible health plan */
  'Health Savings Account (HSA)',

  /* US: employer-sponsored dental/vision coverage, sold separately from medical insurance */
  'Dental & vision insurance',

  /* Canada: registered retirement savings plan with employer matching contributions (RRSP) */
  'RRSP matching',

  /* Canada: employer-sponsored extended health plan covering dental/vision/prescription drugs beyond public healthcare */
  'Extended health & dental coverage',

  /* Shared US + Canada: paid leave beyond the jurisdiction's statutory minimum */
  'Extra paid time off',

  /* EU-general: pre-tax meal voucher schemes codified in several EU states' own labour law,
     e.g. France's titre-restaurant (Code du travail; https://www.service-public.gouv.fr/particuliers/vosdroits/F21059?lang=en),
     Belgium's chèque-repas, Italy's buoni pasto. Distinct from Finland's own Lounasetu regime,
     which stays its own Finland-tier label. */
  'Meal vouchers',

  /* EU-general: employer-subsidised public transport, tax-privileged in several member states,
     e.g. Germany's Jobticket/Deutschlandticket subsidy under EStG §3 Nr. 15
     (https://lsth.bundesfinanzministerium.de/lsth/2025/B-Anhaenge/Anhang-17a/anhang-17a.html).
     Broader concept than Finland's own specific Commuter pass regime, which stays separate. */
  'Public-transport subsidy',

  /* EU-general: employer top-up on top of the statutory/state pension, e.g. Germany's
     betriebliche Altersvorsorge (BetrAVG; https://www.gesetze-im-internet.de/betravg/). */
  'Statutory pension top-up',

  /* UK: workplace pension contribution above the statutory 8%-total/3%-employer auto-enrolment
     minimum (Pensions Act 2008; https://www.gov.uk/workplace-pensions) */
  'Workplace pension (above minimum)',

  /* UK: HMRC-recognised interest-free/low-interest loan for an annual public-transport season
     ticket (https://www.gov.uk/expenses-and-benefits-loans-provided-to-employees) */
  'Season ticket loan',

  /* UK: HMRC tax-advantaged employee share schemes, Share Incentive Plan (SIP) and Save As You
     Earn (SAYE) (https://www.gov.uk/tax-employee-share-schemes) */
  'Share Incentive Plan (SAYE)',
];

/* ---------------- SWEDISH (Finland) ------------------------------------- */
export const BENEFITS_SV: string[] = [
  /* smartum.fi */ 'Lunchförmån',
  /* benefits.workday.com */ 'Motion- och kulturförmån',
  /* edenred.fi */ 'Massageförmån',
  /* epassi.fi */ 'Hälsoplånbok',
  /* vero.fi – commuter */ 'Arbetsresebiljett',
  /* vero.fi – bike */ 'Företagscykel',
  /* stm.fi + kela.fi korvausluokka II */ 'Utökad företagshälsovård',
  /* vero.fi – fringe */ 'Tilläggssjukförsäkring',
  /* edenred.fi */ 'Tjänstetelefon',
  /* edenred.fi */ 'Heminternet',
  /* vero.fi – remote */ 'Distansarbetsersättning',
  /* stm.fi – working hours */ 'Flexibla arbetstider',
  /* edenred.fi */ 'Utbildningsbudget',
  /* edenred.fi */ 'Kontantbonus',
  /* edenred.fi */ 'Aktieoptioner / RSU',
  /* edenred.fi */ 'Tilläggspension',
  /* edenred.fi */ 'Olycksfalls- / livförsäkring',
  /* pam.fi */ 'Semesterpenning',
  /* kela.fi */ 'Barnomsorgsförmån',
  /* vero.fi – in-kind benefits */ 'Tjänstebil',
  /* US */ '401(k)-arbetsgivarmatchning',
  /* US */ 'Hälsokonto (HSA)',
  /* US */ 'Tand- och synförsäkring',
  /* Canada */ 'RRSP-arbetsgivarmatchning',
  /* Canada */ 'Utökad hälso- och tandvårdsförsäkring',
  /* US + Canada */ 'Extra semesterdagar',
  /* EU-general */ 'Måltidskuponger',
  /* EU-general */ 'Kollektivtrafikbidrag',
  /* EU-general */ 'Tillägg till lagstadgad pension',
  /* UK */ 'Tjänstepension (över minimum)',
  /* UK */ 'Säsongsbiljettlån',
  /* UK */ 'Aktieprogram (SAYE)',
];

/* ---------------- FINNISH ------------------------------------------------ */
export const BENEFITS_FI: string[] = [
  /* smartum.fi */ 'Lounasetu',
  /* benefits.workday.com */ 'Liikunta- ja kulttuurietu',
  /* edenred.fi */ 'Hierontaetu',
  /* epassi.fi */ 'Hyvinvointietu',
  /* vero.fi – commuter */ 'Työsuhdematkalippu',
  /* vero.fi – bike */ 'Työsuhdepyörä',
  /* stm.fi + kela.fi korvausluokka II */ 'Laajennettu työterveyshuolto',
  /* vero.fi – fringe */ 'Lisäterveysvakuutus',
  /* edenred.fi */ 'Työpuhelin',
  /* edenred.fi */ 'Kotinetu',
  /* vero.fi – remote */ 'Etätyökorvaus',
  /* stm.fi – working hours */ 'Joustavat työajat',
  /* edenred.fi */ 'Koulutusbudjetti',
  /* edenred.fi */ 'Rahabonus',
  /* edenred.fi */ 'Osakeoptiot / RSU',
  /* edenred.fi */ 'Lisäeläke',
  /* edenred.fi */ 'Tapaturma- / henkivakuutus',
  /* pam.fi */ 'Lomaraha',
  /* kela.fi */ 'Lastenhoitoetu',
  /* vero.fi – in-kind benefits */ 'Työsuhdeauto',
  /* US */ '401(k)-työnantajaosuus',
  /* US */ 'Terveystili (HSA)',
  /* US */ 'Hammas- ja näkövakuutus',
  /* Canada */ 'RRSP-työnantajaosuus',
  /* Canada */ 'Laajennettu terveys- ja hammashoitovakuutus',
  /* US + Canada */ 'Ylimääräiset lomapäivät',
  /* EU-general */ 'Ateriaseteli',
  /* EU-general */ 'Joukkoliikennetuki',
  /* EU-general */ 'Lakisääteisen eläkkeen lisäys',
  /* UK */ 'Työeläke (yli vähimmäistason)',
  /* UK */ 'Kausilippulaina',
  /* UK */ 'Osakeohjelma (SAYE)',
];

/**
 * Which country/region a benefit label makes sense for. 'universal' labels are kept for every
 * job regardless of resolved country; every other tier is kept only when it matches the job's
 * own resolved country (see filterBenefitsToJobCountry in processor-output-fields.ts): a
 * Finland-tier label never survives on a US job and vice versa. Keyed by the canonical ENGLISH
 * label (same index-alignment convention as BENEFIT_GROUNDING_KEYWORDS in
 * ground-benefits-in-source-text.ts) since benefits_en/sv/fi are always position-aligned by the
 * time this is consulted.
 */
export type BenefitRegionTier = 'universal' | 'finland' | 'us' | 'canada' | 'eu_general' | 'uk' | 'sweden';

/**
 * A curated subset of BENEFITS_EN/FI/SV (position-aligned across the three arrays, same
 * convention as those arrays themselves): benefits common enough in the live corpus to actually
 * differentiate postings, plus one deliberate low-frequency-but-high-value pick, chosen for
 * Finland specifically. Selected from live `benefits` facet counts (2026-09-08 snapshot, ~36k
 * active fi-locale listings) plus Finland market context — e.g. 'Extra pension' is rare (~0.7%
 * of listings) but a genuine differentiator since Finland leans almost entirely on the statutory
 * pension, unlike most of the higher-frequency benefits which are closer to table stakes.
 * Used by indexer.ts's calculateJobScore (the `valuedBenefits` weight) to reward postings that
 * disclose particularly meaningful Finnish benefits, distinct from computeBenefitsScore (Employer
 * Score's composite, which counts ALL extracted benefits unweighted, company-wide).
 */
export const VALUED_FINLAND_BENEFITS_EN: string[] = [
  'Flexible working hours',
  'Stock options / RSU',
  'Well-being / healthcare wallet',
  'Lunch benefit',
  'Extended occupational healthcare',
  'Sports & culture benefit',
  'Extra pension',
];
export const VALUED_FINLAND_BENEFITS_FI: string[] = [
  'Joustavat työajat',
  'Osakeoptiot / RSU',
  'Hyvinvointietu',
  'Lounasetu',
  'Laajennettu työterveyshuolto',
  'Liikunta- ja kulttuurietu',
  'Lisäeläke',
];
export const VALUED_FINLAND_BENEFITS_SV: string[] = [
  'Flexibla arbetstider',
  'Aktieoptioner / RSU',
  'Hälsoplånbok',
  'Lunchförmån',
  'Utökad företagshälsovård',
  'Motion- och kulturförmån',
  'Tilläggspension',
];

export const BENEFIT_REGION_TIERS: Record<string, BenefitRegionTier[]> = {
  'Lunch benefit': ['finland'],
  'Sports & culture benefit': ['finland', 'sweden'],
  'Massage benefit': ['finland', 'sweden'],
  'Well-being / healthcare wallet': ['finland'],
  'Commuter pass': ['finland'],
  'Company bike': ['universal'],
  'Extended occupational healthcare': ['finland'],
  'Extra health insurance': ['universal'],
  'Work phone': ['universal'],
  'Home-internet benefit': ['finland'],
  'Remote-work allowance': ['universal'],
  'Flexible working hours': ['universal'],
  'Training budget': ['universal'],
  'Cash bonus': ['universal'],
  'Stock options / RSU': ['universal'],
  'Extra pension': ['universal'],
  'Accident / life insurance': ['universal'],
  'Holiday bonus': ['finland'],
  'Child-care voucher': ['finland'],
  'Company car': ['universal'],
  '401(k) matching': ['us'],
  'Health Savings Account (HSA)': ['us'],
  'Dental & vision insurance': ['us'],
  'RRSP matching': ['canada'],
  'Extended health & dental coverage': ['canada'],
  'Extra paid time off': ['us', 'canada'],
  'Meal vouchers': ['eu_general'],
  'Public-transport subsidy': ['eu_general'],
  'Statutory pension top-up': ['eu_general'],
  'Workplace pension (above minimum)': ['uk'],
  'Season ticket loan': ['uk'],
  'Share Incentive Plan (SAYE)': ['uk'],
};
